/**
 * DataTable Keyboard Navigation Utility
 * ใช้สำหรับ table ที่มี pagination และ sorting เหมือน DataTable.js
 */

import { useEffect } from 'react';

/**
 * Hook สำหรับ keyboard navigation ของ DataTable
 * @param {boolean} isActive - table active หรือไม่
 * @param {Array} currentItems - รายการข้อมูลในหน้าปัจจุบัน
 * @param {number} selectedRowIndex - index ของแถวที่เลือก
 * @param {Function} setSelectedRowIndex - function ตั้งค่า selectedRowIndex
 * @param {Function} onRowSelected - function เมื่อเลือกแถว
 * @param {Object} fieldMappings - การ mapping field สำหรับการอ้างอิง
 * @param {number} currentPage - หน้าปัจจุบัน
 * @param {Function} handlePageChange - function เปลี่ยนหน้า
 * @param {number} totalItems - จำนวนรายการทั้งหมด
 * @param {number} itemsPerPage - จำนวนรายการต่อหน้า
 * @param {Function} getReport - function สำหรับดาวน์โหลดรายงาน (optional)
 * @param {Object} options - ตัวเลือกเพิ่มเติม
 */
export const useDataTableKeyboardNavigation = (
    isActive,
    currentItems,
    selectedRowIndex,
    setSelectedRowIndex,
    onRowSelected,
    fieldMappings,
    currentPage,
    handlePageChange,
    totalItems,
    itemsPerPage,
    getReport = null,
    options = {}
) => {
    const {
        preventInputFocus = true,
        preventModalActive = true,
        customKeyHandlers = {},
        setSelectedRow = null
    } = options;

    useEffect(() => {
        if (!isActive) return;

        const handleKeyDown = (event) => {
            // ป้องกันการทำงานถ้ามี modal เปิดอยู่หรือ focus อยู่ใน input field
            if (preventModalActive && document.querySelector('.modal.show')) return;
            
            if (preventInputFocus && (
                document.activeElement.tagName === 'INPUT' || 
                document.activeElement.tagName === 'TEXTAREA' ||
                document.activeElement.tagName === 'SELECT'
            )) {
                return;
            }

            if (currentItems.length === 0) return;

            // Check for custom key handlers first
            if (customKeyHandlers[event.key]) {
                event.preventDefault();
                customKeyHandlers[event.key](event, {
                    selectedRowIndex,
                    currentItems,
                    currentPage,
                    totalItems,
                    itemsPerPage
                });
                return;
            }

            switch (event.key) {
                case 'ArrowUp':
                    event.preventDefault();
                    setSelectedRowIndex(prevIndex => {
                        const newIndex = prevIndex > 0 ? prevIndex - 1 : currentItems.length - 1;
                        
                        // เลื่อน scroll ไปยังแถวที่เลือก
                        scrollToDataTableRow(newIndex);
                        
                        // อัปเดต selectedRow ถ้ามี function
                        if (setSelectedRow && fieldMappings?.no) {
                            const newRowId = currentItems[newIndex][fieldMappings.no] || newIndex + 1;
                            setSelectedRow(newRowId);
                            localStorage.setItem("selectedRow", newRowId);
                        }
                        
                        return newIndex;
                    });
                    break;

                case 'ArrowDown':
                    event.preventDefault();
                    setSelectedRowIndex(prevIndex => {
                        const newIndex = prevIndex < currentItems.length - 1 ? prevIndex + 1 : 0;
                        
                        // เลื่อน scroll ไปยังแถวที่เลือก
                        scrollToDataTableRow(newIndex);
                        
                        // อัปเดต selectedRow ถ้ามี function
                        if (setSelectedRow && fieldMappings?.no) {
                            const newRowId = currentItems[newIndex][fieldMappings.no] || newIndex + 1;
                            setSelectedRow(newRowId);
                            localStorage.setItem("selectedRow", newRowId);
                        }
                        
                        return newIndex;
                    });
                    break;

                case 'Enter':
                    event.preventDefault();
                    if (currentItems[selectedRowIndex] && onRowSelected) {
                        if (fieldMappings?.no) {
                            onRowSelected(currentItems[selectedRowIndex][fieldMappings.no]);
                        } else {
                            onRowSelected(currentItems[selectedRowIndex]);
                        }
                    }
                    break;

                case 'p':
                case 'P':
                    event.preventDefault();
                    if (currentItems[selectedRowIndex] && getReport && fieldMappings?.no) {
                        getReport(currentItems[selectedRowIndex][fieldMappings.no]);
                    }
                    break;

                case 'ArrowLeft':
                    event.preventDefault();
                    if (currentPage > 1) {
                        handlePageChange(currentPage - 1);
                    }
                    break;

                case 'ArrowRight':
                    event.preventDefault();
                    const totalPages = Math.ceil(totalItems / itemsPerPage);
                    if (currentPage < totalPages) {
                        handlePageChange(currentPage + 1);
                    }
                    break;

                case 'F5':
                    event.preventDefault();
                    // Focus ไปที่ช่องค้นหา - ลองหาหลาย selector
                    let searchInput = document.querySelector('input[placeholder*="ค้นหา"]');
                    if (!searchInput) {
                        searchInput = document.querySelector('.navbar-form input[type="text"]');
                    }
                    if (!searchInput) {
                        searchInput = document.querySelector('input[type="text"]');
                    }
                    if (searchInput) {
                        searchInput.focus();
                        searchInput.select(); // เลือกข้อความทั้งหมดในช่องค้นหา
                    }
                    break;

                case 'Escape':
                    event.preventDefault();
                    // เคลียร์ช่องค้นหา - ลองหาหลาย selector
                    let searchInputEsc = document.querySelector('input[placeholder*="ค้นหา"]');
                    if (!searchInputEsc) {
                        searchInputEsc = document.querySelector('.navbar-form input[type="text"]');
                    }
                    if (!searchInputEsc) {
                        searchInputEsc = document.querySelector('input[type="text"]');
                    }
                    if (searchInputEsc && searchInputEsc.value) {
                        searchInputEsc.value = '';
                        // Trigger change event เพื่อให้ React component อัปเดต
                        const changeEvent = new Event('input', { bubbles: true });
                        searchInputEsc.dispatchEvent(changeEvent);
                        // กลับไป focus ที่ table
                        searchInputEsc.blur();
                        setSelectedRowIndex(0); // รีเซ็ตไปแถวแรก
                    }
                    break;

                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [
        isActive,
        currentItems,
        selectedRowIndex,
        onRowSelected,
        fieldMappings,
        currentPage,
        handlePageChange,
        totalItems,
        itemsPerPage,
        getReport,
        customKeyHandlers,
        setSelectedRow,
        preventInputFocus,
        preventModalActive
    ]);
};

/**
 * ฟังก์ชันสำหรับ styling แถวที่เลือก
 * @param {number} index - index ของแถว
 * @param {number} selectedRowIndex - index ของแถวที่เลือก
 * @param {string} selectedDocument - document ที่เลือก (optional)
 * @param {Object} data - ข้อมูลของแถว (optional)
 * @param {string} idField - field ที่ใช้เป็น id (optional)
 * @returns {Object} - style object
 */
export const getDataTableRowStyle = (index, selectedRowIndex, selectedDocument = null, data = null, idField = null) => {
    const isHighlighted = index === selectedRowIndex;
    const isSelected = selectedDocument && data && idField && selectedDocument === data[idField];
    
    let backgroundColor = 'transparent';
    
    if (isSelected) {
        backgroundColor = '#e3f2fd'; // สีฟ้าอ่อนสำหรับแถวที่เลือก
    }
    
    if (isHighlighted) {
        backgroundColor = 'rgb(105, 188, 216)'; // สีฟ้าเข้มสำหรับแถวที่ highlight
    }
    
    return {
        backgroundColor,
        cursor: 'pointer',
        transition: 'background-color 0.2s ease'
    };
};

/**
 * ฟังก์ชันสำหรับสร้าง keyboard instruction text สำหรับ DataTable
 * @param {boolean} hasReport - มีฟังก์ชัน report หรือไม่
 * @param {boolean} hasPagination - มี pagination หรือไม่
 * @returns {string} - คำแนะนำการใช้คีย์บอร์ด
 */
export const getDataTableKeyboardInstructions = (hasReport = false, hasPagination = true) => {
    let instructions = "คีย์บอร์ด: ↑↓ (เลือกแถว) | Enter (เข้าดูรายละเอียด)";
    
    if (hasReport) {
        instructions += " | P (ดาวน์โหลดรายงาน)";
    }
    
    if (hasPagination) {
        instructions += " | ←→ (เปลี่ยนหน้า)";
    }
    
    return instructions + " | F5 (ค้นหา)";
};

/**
 * ฟังก์ชันสำหรับเลื่อน scroll ไปยังแถวที่เลือกใน DataTable
 * @param {number} rowIndex - index ของแถว
 */
export const scrollToDataTableRow = (rowIndex) => {
    setTimeout(() => {
        // ลำดับความสำคัญในการค้นหา table container สำหรับ DataTable
        let tableContainer = null;
        let selectedRow = null;
        
        // 1. ค้นหาใน table-responsive ทั่วไป
        tableContainer = document.querySelector('.table-responsive');
        if (tableContainer) {
            selectedRow = document.querySelector(`tbody tr:nth-child(${rowIndex + 1})`);
        }
        
        // 2. ถ้าไม่เจอ ลองค้นหาแบบ specific ของ basic-datatables
        if (!tableContainer || !selectedRow) {
            tableContainer = document.querySelector('#basic-datatables')?.closest('.table-responsive');
            selectedRow = document.querySelector(`#basic-datatables tbody tr:nth-child(${rowIndex + 1})`);
        }
        
        // 3. ถ้ายังไม่เจอ ลองค้นหาโดยใช้ class card
        if (!tableContainer || !selectedRow) {
            const cardElement = document.querySelector('.card .table-responsive');
            if (cardElement) {
                tableContainer = cardElement;
                selectedRow = cardElement.querySelector(`tbody tr:nth-child(${rowIndex + 1})`);
            }
        }
        
        if (tableContainer && selectedRow) {
            // คำนวณตำแหน่งที่ต้องเลื่อน
            const rowTop = selectedRow.offsetTop;
            const rowHeight = selectedRow.offsetHeight;
            const containerHeight = tableContainer.clientHeight;
            const scrollTop = tableContainer.scrollTop;
            
            // ตรวจสอบว่าแถวอยู่นอกเขตมองเห็นหรือไม่
            if (rowTop < scrollTop) {
                // แถวอยู่เหนือเขตมองเห็น - เลื่อนขึ้น
                tableContainer.scrollTop = Math.max(0, rowTop - 20); // เผื่อ padding
            } else if (rowTop + rowHeight > scrollTop + containerHeight) {
                // แถวอยู่ใต้เขตมองเห็น - เลื่อนลง
                tableContainer.scrollTop = rowTop - containerHeight + rowHeight + 20; // เผื่อ padding
            }
        }
    }, 10);
};
